import React, { useState, useEffect, useRef } from 'react';
import './TimeSelector.css';

interface TimeSelectorProps {
    value: string;
    onChange: (time: string) => void;
    disabledTimes?: string[]; // Times to disable based on start/end validation
    otherBlocks?: { startTime: string, endTime: string }[]; // Other time blocks in the same day
    isStartSelector?: boolean; // Whether this is a start time selector
}

const TimeSelector: React.FC<TimeSelectorProps> = ({
    value,
    onChange,
    disabledTimes = [],
    otherBlocks = [],
    isStartSelector = true
}) => {
    const selectRef = useRef<HTMLSelectElement>(null);

    // Parse the 24h time string into display format (handles .5 increments)
    const parseTime = (timeStr: string): string => {
        if (!timeStr || timeStr === "--") return "--";
        const [hours, minutes] = timeStr.split(':').map(Number);
        const totalHours = hours + (minutes / 60);
        if (totalHours === 0) return "12 AM";
        if (totalHours === 24 || (hours === 23 && minutes === 59)) return "12 A+1"; // Handle end of day case
        if (totalHours === 12) return "12 PM";
        const displayHour = Math.floor(totalHours) % 12 || 12;
        const period = totalHours >= 12 ? 'PM' : 'AM';
        if (totalHours % 1 !== 0) {
            // Show as 10:30 A/P (shorter for .5 increments)
            const shortPeriod = period === 'AM' ? 'A' : 'P';
            return `${displayHour}:30 ${shortPeriod}`;
        }
        return `${displayHour} ${period}`;
    };

    // Convert display format back to 24h time string (handles .5 increments)
    const formatTime = (displayTime: string): string => {
        if (displayTime === "--") return "";
        if (displayTime === "12 A+1") return "23:59"; // Send 23:59 to backend for end of day
        if (displayTime.includes(":30")) {
            // e.g. 10:30 A or 10:30 P
            const [hourPart, rest] = displayTime.split(":");
            const [minute, period] = rest.split(" ");
            let hour = parseInt(hourPart);
            // Convert short period to full period
            const fullPeriod = period === 'A' ? 'AM' : 'PM';
            if (fullPeriod === 'PM' && hour !== 12) hour += 12;
            if (fullPeriod === 'AM' && hour === 12) hour = 0;
            return `${hour.toString().padStart(2, '0')}:30`;
        }
        // e.g. 10 AM
        const [hour, period] = displayTime.split(' ');
        let hours = parseInt(hour);
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        return `${hours.toString().padStart(2, '0')}:00`;
    };

    // Convert 24h format to display format for disabled times
    const convertTo12Hour = (time24: string): string => {
        const [hours, minutes] = time24.split(':').map(Number);
        const totalHours = hours + (minutes / 60);
        if (totalHours === 0) return "12 AM";
        if (totalHours === 24 || (hours === 23 && minutes === 59)) return "12 A+1"; // Handle end of day case
        if (totalHours === 12) return "12 PM";
        const displayHour = Math.floor(totalHours) % 12 || 12;
        const period = totalHours >= 12 ? 'PM' : 'AM';
        if (minutes === 30) {
            // Use short format for .5 increments in disabled times too
            const shortPeriod = period === 'AM' ? 'A' : 'P';
            return `${displayHour}:30 ${shortPeriod}`;
        }
        return `${displayHour} ${period}`;
    };

    // Get times that should be disabled due to other blocks
    const getOverlapDisabledTimes = (): string[] => {
        const overlapTimes: string[] = [];
        otherBlocks.forEach(block => {
            if (!block.startTime || !block.endTime) return;
            const startHour = parseInt(block.startTime.split(':')[0]);
            const startMinutes = parseInt(block.startTime.split(':')[1]) || 0;
            const endHour = block.endTime === "23:59" ? 24 : parseInt(block.endTime.split(':')[0]);
            const endMinutes = block.endTime === "23:59" ? 0 : (parseInt(block.endTime.split(':')[1]) || 0);
            const startTotalMinutes = startHour * 60 + startMinutes;
            const endTotalMinutes = endHour * 60 + endMinutes;
            for (let minutes = startTotalMinutes; minutes < endTotalMinutes; minutes += 30) {
                const hour = Math.floor(minutes / 60);
                const minute = minutes % 60;
                const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                overlapTimes.push(timeStr);
            }
        });
        return overlapTimes;
    };

    // Combine both types of disabled times
    const allDisabledTimes = [
        ...disabledTimes,
        ...getOverlapDisabledTimes()
    ];

    // Convert disabled times from 24h to 12h format, handling the special case
    const disabledDisplay = allDisabledTimes.map(time => {
        const [hours, minutes] = time.split(':').map(Number);
        if (hours === 0 && minutes === 0) return "12 AM";
        if (hours === 24 && minutes === 0) return "12 A+1";
        if (hours === 23 && minutes === 59) return "12 A+1";
        if (hours === 12 && minutes === 0) return "12 PM";
        const displayHour = Math.floor(hours % 12) || 12;
        const period = hours >= 12 ? 'PM' : 'AM';
        if (minutes === 30) {
            // Use short format for .5 increments
            const shortPeriod = period === 'AM' ? 'A' : 'P';
            return `${displayHour}:30 ${shortPeriod}`;
        }
        return `${displayHour} ${period}`;
    });

    const [displayTime, setDisplayTime] = useState(parseTime(value));

    useEffect(() => {
        setDisplayTime(parseTime(value));
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newDisplayTime = e.target.value;
        setDisplayTime(newDisplayTime);
        onChange(formatTime(newDisplayTime));
    };

    // Check if a time would be disabled
    const isTimeDisabled = (timeStr: string): boolean => {
        if (timeStr === "--") return false;
        return disabledDisplay.includes(timeStr);
    };

    // Handle keyboard navigation for .5 increments
    const handleKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const currentTime = value;
            if (!currentTime || currentTime === "") return;

            const [hours, minutes] = currentTime.split(':').map(Number);
            const totalMinutes = hours * 60 + minutes;

            let newTotalMinutes;
            if (e.key === 'ArrowUp') {
                newTotalMinutes = totalMinutes + 30; // Add 30 minutes (.5 hours)
            } else {
                newTotalMinutes = totalMinutes - 30; // Subtract 30 minutes (.5 hours)
            }

            // Handle wrap around and special cases
            if (newTotalMinutes >= 24 * 60) {
                // If we're going past 24 hours, go to 23:59 (12 A+1)
                newTotalMinutes = 23 * 60 + 59;
            } else if (newTotalMinutes < 0) {
                // If we're going below 0, go to 23:30
                newTotalMinutes = 23 * 60 + 30;
            }

            const newHours = Math.floor(newTotalMinutes / 60);
            const newMinutes = newTotalMinutes % 60;
            const newTime = `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;

            // Check if the new time would be disabled
            const newDisplayTime = parseTime(newTime);
            if (!isTimeDisabled(newDisplayTime)) {
                onChange(newTime);
            }
        }
    };

    // Generate all time options with 12 AM at both top and bottom (only whole hours for dropdown)
    const timeOptions = [
        "--",
        "12 AM", // Start of day
        ...Array.from({ length: 23 }, (_, i) => {
            const hour = (i + 1) % 12 || 12;
            const period = i + 1 >= 12 ? 'PM' : 'AM';
            return `${hour} ${period}`;
        }),
        "12 A+1" // End of day
    ];

    // If the current displayTime is not in the dropdown, add it as a temporary option (for .5 increments)
    const optionsToRender = timeOptions.includes(displayTime)
        ? timeOptions
        : [displayTime, ...timeOptions];

    return (
        <div className="time-selector">
            <select
                ref={selectRef}
                value={displayTime}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="time-select"
            >
                {optionsToRender.map(option => {
                    const isNextDayMidnight = option === "12 A+1";
                    // For the end-of-day 12 A+1, check if it should be disabled based on 24:00
                    const shouldBeDisabled = option !== "--" && (
                        isNextDayMidnight
                            ? disabledDisplay.includes("12 A+1")
                            : disabledDisplay.includes(option)
                    );
                    return (
                        <option
                            key={option}
                            value={option}
                            disabled={shouldBeDisabled}
                        >
                            {option}
                        </option>
                    );
                })}
            </select>
        </div>
    );
};

export default TimeSelector; 
